import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plus,
  Radio,
  Loader2,
  Users,
  Eye,
  ShieldAlert,
  Send,
  Lock,
  BadgeCheck,
  Share2,
  Compass,
  Pencil,
} from "lucide-react";
import { api } from "@/lib/api";
import { shieldBlockedMessage } from "@/lib/shield";
import { LinkChips } from "@/components/common/LinkChips";
import { VerifiedCheck } from "@/components/common/TrustBadge";
import { LimitMeter } from "@/components/common/LimitMeter";
import { timeAgo, clockTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LinkPreview } from "@/components/chat/LinkPreview";

const SHARE_BASE = process.env.REACT_APP_SHARE_BASE || "https://bloqtalk.app";

function ChannelIcon({ emoji, size = "md" }) {
  const dim = size === "lg" ? "h-14 w-14 text-2xl" : "h-12 w-12 text-xl";
  return (
    <div className={cn("flex shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-bloq-s2", dim)}>
      {emoji || "📣"}
    </div>
  );
}

function TokenGatePill({ gate }) {
  if (!gate) return null;
  const label = gate.symbol
    ? `${gate.min_amount || ""} ${gate.symbol}`.trim()
    : `${gate.kind} · ${gate.chain}`;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md border border-bloq-purple/40 bg-bloq-purple/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-bloq-purple"
      data-testid="channel-token-gate"
    >
      <Lock className="h-2.5 w-2.5" /> {label}
    </span>
  );
}

function CreateChannelDialog({ open, onOpenChange, canCreate, minScore, score }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", handle: "", emoji: "📣", description: "" });

  const createMut = useMutation({
    mutationFn: (payload) => api.createChannel(payload),
    onSuccess: (channel) => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      toast.success("Channel created");
      onOpenChange(false);
      setForm({ name: "", handle: "", emoji: "📣", description: "" });
      if (channel?.id) navigate(`/app/channels/${channel.id}`);
    },
    onError: (e) => {
      const d = e?.response?.data?.detail;
      const blocked = shieldBlockedMessage(e);
      if (blocked) {
        toast.error(blocked);
      } else if (d?.code === "creation_limit") {
        toast.error(d.message || `You've reached your channel limit (${d.max}) — upgrade to create more.`);
      } else {
        toast.error((typeof d === "string" && d) || d?.message || "Could not create channel");
      }
    },
  });

  const submit = () => {
    if (form.name.trim().length < 3) return toast.error("Name must be at least 3 characters");
    if (form.handle.trim().length < 3) return toast.error("Handle must be at least 3 characters");
    createMut.mutate({
      name: form.name.trim(),
      handle: form.handle.trim(),
      emoji: form.emoji || "📣",
      description: form.description.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="create-channel-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Create a Channel</DialogTitle>
          <DialogDescription className="text-bloq-muted">
            Broadcast to subscribers. Every post is Shield-screened for scams.
          </DialogDescription>
        </DialogHeader>
        {!canCreate ? (
          <div className="rounded-xl border border-bloq-purple/30 bg-bloq-purple/10 p-4 text-sm text-bloq-purple" data-testid="channel-create-gate">
            <ShieldAlert className="mb-1 h-4 w-4" />
            You need a BLOQ Score of {minScore} to create a channel. Yours is {score}. Keep building trust to unlock broadcasting.
          </div>
        ) : (
          <div className="space-y-4">
            <LimitMeter slot="channels" label="channels created" unit="channels" testid="channel-create-meter" />
            <div className="flex gap-3">
              <div className="w-20">
                <Label className="text-xs text-bloq-muted">Emoji</Label>
                <Input
                  value={form.emoji}
                  onChange={(e) => setForm((f) => ({ ...f, emoji: e.target.value }))}
                  className="mt-1 h-11 rounded-xl border-white/12 bg-bloq-bg text-center text-xl"
                  maxLength={4}
                  data-testid="channel-emoji-input"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-bloq-muted">Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Alpha Signals"
                  maxLength={50}
                  className="mt-1 h-11 rounded-xl border-white/12 bg-bloq-bg"
                  data-testid="channel-name-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-bloq-muted">Handle</Label>
              <div className="mt-1 flex items-center rounded-xl border border-white/12 bg-bloq-bg pl-3">
                <span className="text-bloq-muted">@</span>
                <Input
                  value={form.handle}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, handle: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") }))
                  }
                  placeholder="alphasignals"
                  maxLength={24}
                  className="h-11 border-0 bg-transparent px-1 focus-visible:ring-0"
                  data-testid="channel-handle-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-bloq-muted">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="What will you broadcast?"
                maxLength={240}
                className="mt-1 rounded-xl border-white/12 bg-bloq-bg"
                data-testid="channel-description-input"
              />
            </div>
          </div>
        )}
        {canCreate && (
          <DialogFooter>
            <Button
              onClick={submit}
              disabled={createMut.isPending}
              className="w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90"
              data-testid="create-channel-submit-btn"
            >
              {createMut.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create Channel"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditChannelDialog({ open, onOpenChange, channel }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: channel.name || "",
    emoji: channel.emoji || "📣",
    description: channel.description || "",
  });

  const editMut = useMutation({
    mutationFn: (payload) => api.updateChannel(channel.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channel-posts", channel.id] });
      qc.invalidateQueries({ queryKey: ["channels"] });
      toast.success("Channel updated");
      onOpenChange(false);
    },
    onError: (e) => toast.error(shieldBlockedMessage(e) || e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not update channel"),
  });

  const submit = () => {
    if (form.name.trim().length < 3) return toast.error("Name must be at least 3 characters");
    editMut.mutate({
      name: form.name.trim(),
      emoji: form.emoji || "📣",
      description: form.description.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white" data-testid="edit-channel-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Channel</DialogTitle>
          <DialogDescription className="text-bloq-muted">
            Update how your channel appears. Your handle stays the same.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="w-20">
              <Label className="text-xs text-bloq-muted">Emoji</Label>
              <Input
                value={form.emoji}
                onChange={(e) => setForm((f) => ({ ...f, emoji: e.target.value }))}
                className="mt-1 h-11 rounded-xl border-white/12 bg-bloq-bg text-center text-xl"
                maxLength={4}
                data-testid="edit-channel-emoji-input"
              />
            </div>
            <div className="flex-1">
              <Label className="text-xs text-bloq-muted">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={50}
                className="mt-1 h-11 rounded-xl border-white/12 bg-bloq-bg"
                data-testid="edit-channel-name-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-bloq-muted">Handle</Label>
            <div className="mt-1 flex items-center rounded-xl border border-white/10 bg-bloq-bg/60 pl-3 opacity-60">
              <span className="text-bloq-muted">@</span>
              <Input
                value={channel.handle}
                disabled
                className="h-11 border-0 bg-transparent px-1 focus-visible:ring-0"
                data-testid="edit-channel-handle-input"
              />
              <Lock className="mr-3 h-3.5 w-3.5 text-bloq-muted" />
            </div>
            <p className="mt-1 text-[11px] text-bloq-muted">Handle can't be changed.</p>
          </div>
          <div>
            <Label className="text-xs text-bloq-muted">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              maxLength={240}
              className="mt-1 rounded-xl border-white/12 bg-bloq-bg"
              data-testid="edit-channel-description-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={editMut.isPending}
            className="w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90"
            data-testid="edit-channel-submit-btn"
          >
            {editMut.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MyChannelRow({ channel, activeId, onOpen }) {
  return (
    <button
      onClick={() => onOpen(channel)}
      className={cn(
        "flex w-full items-center gap-3 rounded-2xl p-3 text-left transition-colors",
        channel.id === activeId ? "bg-white/8" : "hover:bg-white/5",
      )}
      data-testid={`my-channel-${channel.id}`}
    >
      <ChannelIcon emoji={channel.emoji} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-semibold">{channel.name}</span>
          {channel.verified && <VerifiedCheck size={13} />}
          <TokenGatePill gate={channel.token_gate} />
        </div>
        <p className="mt-0.5 truncate text-sm text-bloq-muted">
          {channel.last_post?.text || channel.description}
        </p>
      </div>
      <div className="flex flex-col items-end gap-1">
        <span className="text-[11px] text-bloq-muted">{timeAgo(channel.last_post?.at || channel.created_at)}</span>
        {channel.is_owner && (
          <span className="rounded-md border border-bloq-mint/40 bg-bloq-mint/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-bloq-mint">
            Owner
          </span>
        )}
      </div>
    </button>
  );
}

function DiscoverChannelCard({ channel, onOpen }) {
  const qc = useQueryClient();
  const subMut = useMutation({
    mutationFn: () => api.subscribeChannel(channel.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      toast.success(`Subscribed to ${channel.name}`);
      onOpen(channel);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not subscribe"),
  });

  return (
    <div className="rounded-2xl border border-white/8 bg-bloq-s1 p-4" data-testid={`discover-channel-${channel.id}`}>
      <button onClick={() => onOpen(channel)} className="flex w-full items-start gap-3 text-left">
        <ChannelIcon emoji={channel.emoji} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-semibold">{channel.name}</span>
            {channel.verified && <VerifiedCheck size={12} />}
            <TokenGatePill gate={channel.token_gate} />
          </div>
          <div className="mt-0.5 text-xs text-bloq-muted">@{channel.handle}</div>
          <div className="mt-1 flex items-center gap-3 text-xs text-bloq-muted">
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" /> {channel.subscriber_count}
            </span>
            <span className="flex items-center gap-1">
              <Radio className="h-3 w-3" /> {channel.post_count} posts
            </span>
          </div>
        </div>
      </button>
      <p className="mt-3 text-sm text-bloq-muted">{channel.description}</p>
      <Button
        onClick={() => subMut.mutate()}
        disabled={subMut.isPending}
        variant="outline"
        className="mt-3 w-full rounded-full border-bloq-mint/60 bg-transparent text-bloq-mint hover:bg-bloq-mint/10"
        data-testid={`subscribe-channel-${channel.id}`}
      >
        {subMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe"}
      </Button>
    </div>
  );
}

function ChannelsList({ activeId }) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["channels"], queryFn: api.listChannels });

  const openChannel = (channel) => navigate(`/app/channels/${channel.id}`);

  return (
    <div className="flex h-full min-h-0 w-full flex-col border-r border-white/8 bg-bloq-s1 md:w-[380px] md:shrink-0">
      <div className="flex items-center justify-between px-5 pb-2 pt-5">
        <div>
          <h1 className="text-2xl font-bold">Channels</h1>
          <div className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-bloq-blue">
            <Radio className="h-3 w-3" /> Verified broadcasts
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/app/discover?tab=channels")}
            className="flex h-10 w-10 items-center justify-center rounded-2xl border border-white/12 text-bloq-blue transition-colors hover:bg-bloq-blue/10"
            data-testid="discover-channels-btn"
            title="Discover"
          >
            <Compass className="h-5 w-5" />
          </button>
          <button
            onClick={() => setDialogOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-2xl bloq-gradient text-bloq-bg transition-opacity hover:opacity-90"
            data-testid="create-channel-btn"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 pb-6">
        {isLoading ? (
          <div className="flex justify-center py-10 text-bloq-muted">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            {data?.mine?.length > 0 && (
              <div>
                <div className="px-2 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-bloq-muted">
                  Subscribed
                </div>
                {data.mine.map((c) => (
                  <MyChannelRow key={c.id} channel={c} activeId={activeId} onOpen={openChannel} />
                ))}
              </div>
            )}
            {data?.discover?.length > 0 && (
              <div>
                <div className="px-2 pb-2 pt-2 text-[11px] font-bold uppercase tracking-wider text-bloq-muted">
                  Discover
                </div>
                <div className="space-y-3 px-1">
                  {data.discover.map((c) => (
                    <DiscoverChannelCard key={c.id} channel={c} onOpen={openChannel} />
                  ))}
                </div>
              </div>
            )}
            {!data?.mine?.length && !data?.discover?.length && (
              <div className="px-4 py-14 text-center">
                <Radio className="mx-auto mb-3 h-10 w-10 text-bloq-muted/60" />
                <p className="text-sm text-bloq-muted">No channels yet.</p>
              </div>
            )}
          </>
        )}
      </div>

      <CreateChannelDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        canCreate={data?.can_create}
        minScore={data?.create_min_score}
        score={data?.score}
      />
    </div>
  );
}

function PostCard({ post }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-bloq-s1 p-4" data-testid={`channel-post-${post.id}`}>
      <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-white">{post.text}</p>
      {(post.links || []).map((l, i) => <LinkPreview key={i} link={l} testid={`channel-post-link-${post.id}-${i}`} />)}
      {post.scam_intent && (
        <div className="mt-3 flex items-center gap-1.5 rounded-lg border border-destructive/40 bg-destructive/10 px-2.5 py-1.5 text-xs font-semibold text-destructive">
          <ShieldAlert className="h-3.5 w-3.5" /> Shield flagged possible scam intent
        </div>
      )}
      <div className="mt-3 flex items-center gap-4 text-[11px] text-bloq-muted">
        <span>{clockTime(post.created_at)} · {timeAgo(post.created_at)}</span>
        <span className="flex items-center gap-1">
          <Eye className="h-3 w-3" /> {post.views}
        </span>
      </div>
    </div>
  );
}

function PostComposer({ channelId }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const postMut = useMutation({
    mutationFn: () => api.createChannelPost(channelId, text.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channel-posts", channelId] });
      qc.invalidateQueries({ queryKey: ["channels"] });
      setText("");
      toast.success("Broadcast sent");
      // Link previews are fetched server-side ~1-2s after posting — refetch to upgrade the post.
      setTimeout(() => qc.invalidateQueries({ queryKey: ["channel-posts", channelId] }), 2500);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not post"),
  });

  return (
    <div className="border-t border-white/8 bg-bloq-s1 p-3">
      <div className="flex items-end gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Broadcast to your subscribers…"
          maxLength={4000}
          rows={1}
          className="min-h-[44px] resize-none rounded-2xl border-white/12 bg-bloq-bg"
          data-testid="channel-post-input"
        />
        <Button
          onClick={() => text.trim() && postMut.mutate()}
          disabled={postMut.isPending || !text.trim()}
          className="h-11 w-11 shrink-0 rounded-2xl bloq-gradient p-0 text-bloq-bg hover:opacity-90"
          data-testid="channel-post-submit-btn"
        >
          {postMut.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </Button>
      </div>
    </div>
  );
}

function ChannelFeed({ channelId, onBack }) {
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["channel-posts", channelId],
    queryFn: () => api.getChannelPosts(channelId),
  });
  const channel = data?.channel;

  const subMut = useMutation({
    mutationFn: () =>
      channel?.subscribed ? api.unsubscribeChannel(channelId) : api.subscribeChannel(channelId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["channel-posts", channelId] });
      qc.invalidateQueries({ queryKey: ["channels"] });
      toast.success(channel?.subscribed ? "Unsubscribed" : "Subscribed");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Action failed"),
  });

  const shareChannel = async () => {
    const url = `${SHARE_BASE}/api/c/${channel.handle}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: channel.name, text: `Follow ${channel.name} on BLOQ Talk`, url });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return; // user dismissed the native share sheet
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Channel link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (isLoading || !channel) {
    return (
      <div className="flex h-full flex-1 items-center justify-center bg-bloq-bg">
        <Loader2 className="h-6 w-6 animate-spin text-bloq-muted" />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col bg-bloq-bg" data-testid="channel-feed">
      <div className="flex items-center gap-3 border-b border-white/8 bg-bloq-s1 px-4 py-3">
        <button
          onClick={onBack}
          className="text-bloq-muted hover:text-white md:hidden"
          data-testid="channel-back-btn"
        >
          ←
        </button>
        <ChannelIcon emoji={channel.emoji} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate font-semibold">{channel.name}</span>
            {channel.verified && <VerifiedCheck size={13} />}
            <TokenGatePill gate={channel.token_gate} />
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-bloq-muted">
            <span>@{channel.handle}</span>
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" /> {channel.subscriber_count}
            </span>
          </div>
        </div>
        {channel.is_owner ? (
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 rounded-full border border-bloq-mint/40 bg-bloq-mint/15 px-3 py-1.5 text-xs font-bold text-bloq-mint">
              <BadgeCheck className="h-3.5 w-3.5" /> Owner
            </span>
            <button
              onClick={() => setEditOpen(true)}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/12 text-bloq-muted transition-colors hover:border-bloq-mint/40 hover:text-bloq-mint"
              data-testid="channel-edit-btn"
              title="Edit channel"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <Button
            onClick={() => subMut.mutate()}
            disabled={subMut.isPending}
            variant={channel.subscribed ? "outline" : "default"}
            className={cn(
              "rounded-full font-bold",
              channel.subscribed
                ? "border-white/15 bg-transparent text-bloq-muted hover:bg-white/5"
                : "bloq-gradient text-bloq-bg hover:opacity-90",
            )}
            data-testid="channel-subscribe-toggle-btn"
          >
            {subMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : channel.subscribed ? (
              "Subscribed"
            ) : (
              "Subscribe"
            )}
          </Button>
        )}
        <button
          onClick={shareChannel}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/12 text-bloq-muted transition-colors hover:text-white"
          data-testid="channel-share-btn"
          title="Share channel"
        >
          <Share2 className="h-4 w-4" />
        </button>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {channel.description && (
          <div className="rounded-2xl border border-white/8 bg-bloq-s1/50 p-4 text-sm text-bloq-muted">
            {channel.description}
            <LinkChips links={channel.links} testid="channel-description-links" className="mt-2" />
          </div>
        )}
        {data.posts?.length ? (
          data.posts.map((p) => <PostCard key={p.id} post={p} />)
        ) : (
          <div className="py-14 text-center text-sm text-bloq-muted">No posts yet.</div>
        )}
      </div>

      {channel.is_owner && <PostComposer channelId={channelId} />}
      {editOpen && <EditChannelDialog open onOpenChange={setEditOpen} channel={channel} />}
    </div>
  );
}

export default function ChannelsPage() {
  const { channelId } = useParams();
  const navigate = useNavigate();

  return (
    <div className="flex h-full min-h-0 w-full" data-testid="channels-page">
      <div className={cn("h-full min-h-0", channelId ? "hidden md:flex" : "flex w-full")}>
        <ChannelsList activeId={channelId} />
      </div>
      <div className={cn("h-full min-h-0 min-w-0 flex-1", channelId ? "flex" : "hidden md:flex")}>
        {channelId ? (
          <ChannelFeed channelId={channelId} onBack={() => navigate("/app/channels")} />
        ) : (
          <div className="hidden flex-1 items-center justify-center bg-bloq-bg md:flex">
            <div className="text-center text-bloq-muted">
              <Radio className="mx-auto mb-3 h-12 w-12 opacity-40" />
              <p>Select a channel to read its broadcasts</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
